import React, { useContext, useEffect, useRef } from 'react';
import { Modal, useModal } from '@nebula/modal';
import { ModalAction } from '@nebula/modal/dist/types/types/ModalProps';
import {
  ChangePaymentMethodDrawerDesc,
  ChangePaymentMethodDrawerTitle,
  SpinnerContainer,
  StyledDrawer,
  WarningContainer,
} from './index.styles';
import { PAYMENT_STAGES, PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';
import PaymentError from '../../../components/PaymentCaptureMethod/PaymentError';
import PaymentOrchestrator from '../../../components/PaymentOrchestrator';
import { useSelector } from 'react-redux';
import Spinner from '@nebula/spinner';

export interface ChangePaymentMethodDrawerProps {
  isShowing: boolean;
  toggleDrawer: () => void;
  returnFocus?: (suggestedNode: Element) => boolean;
}

export interface ChangePaymentMethodDrawerContentProps {
  toggleDrawer: () => void;
  toggleModal: () => void;
  isModalShowing: boolean;
}

const ChangePaymentMethodDrawer: React.FC<ChangePaymentMethodDrawerProps> = ({
  isShowing,
  toggleDrawer,
  returnFocus,
}) => {
  const { isShowing: isModalShowing, toggle: toggleModal } = useModal();
  const paymentContext = useContext(PaymentContext);
  const { paymentStage } = paymentContext;
  const handleDrawerClose = () => {
    if (paymentStage === PAYMENT_STAGES.PAYMENT_METHOD_UPDATED || paymentStage === PAYMENT_STAGES.SERVICE_UNAVAILABLE) {
      toggleDrawer();
    } else {
      toggleModal();
    }
  };

  return (
    <StyledDrawer
      isShowing={isShowing}
      toggleHide={handleDrawerClose}
      zIndex={10}
      hideOverflow={
        paymentStage !== PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD &&
        paymentStage !== PAYMENT_STAGES.REQUIRES_AUTHORIZATION
      }
      disableClickOutside={true}
      returnFocus={returnFocus}
    >
      <ChangePaymentMethodDrawerContent
        toggleDrawer={toggleDrawer}
        toggleModal={toggleModal}
        isModalShowing={isModalShowing}
      />
    </StyledDrawer>
  );
};

export const ChangePaymentMethodDrawerContent: React.FC<ChangePaymentMethodDrawerContentProps> = ({
  toggleDrawer,
  toggleModal,
  isModalShowing,
}) => {
  const paymentContext = useContext(PaymentContext);
  const { paymentErrors, upgradeOffer, paymentStage, isWebview, clearPaymentErrors } = paymentContext;
  const splitReady = useSelector((state: any) => state.split?.fetched);
  const errorRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const paymentError = paymentErrors.length ? paymentErrors[paymentErrors.length - 1] : null;

  // Focus on error alert when it appears
  useEffect(() => {
    let focusTimeout: ReturnType<typeof setTimeout>;
    if (paymentError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth' });
      // Wait for smooth scroll animation to complete before focusing
      focusTimeout = setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.focus();
        }
      }, 500);
    }
    return () => {
      clearTimeout(focusTimeout);
    };
  }, [paymentError]);

  // Clean up close timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleConfirmClose = () => {
    // Close both modal and drawer
    toggleModal();
    closeTimeoutRef.current = setTimeout(() => {
      toggleDrawer();
      clearPaymentErrors();
    }, 100);
  };

  const handleCancelClose = () => {
    // Only close the modal, keep drawer open
    toggleModal();
  };

  const modalActions: ModalAction[] = [
    {
      label: 'Yes, cancel',
      onClick: handleConfirmClose,
      buttonKind: 'secondaryMagenta',
    },
    {
      label: 'No, continue',
      onClick: handleCancelClose,
      buttonKind: 'secondaryMagenta',
    },
  ];

  return (
    <React.Fragment>
      {paymentError && (
        <WarningContainer ref={errorRef} tabIndex={-1} role="alert">
          <PaymentError error={paymentError} />
        </WarningContainer>
      )}
      {(paymentStage === PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD ||
        paymentStage === PAYMENT_STAGES.REQUIRES_AUTHORIZATION) && (
        <ChangePaymentMethodDrawerTitle authorizationPage={paymentStage === PAYMENT_STAGES.REQUIRES_AUTHORIZATION}>
          Change payment method
        </ChangePaymentMethodDrawerTitle>
      )}
      {paymentStage === PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD && (
        <ChangePaymentMethodDrawerDesc>
          We’ll use this payment method for your monthly subscription and any other payments you make.
        </ChangePaymentMethodDrawerDesc>
      )}

      {splitReady ? (
        <PaymentOrchestrator
          isMethodChange={true}
          isTrial={false}
          isWebview={isWebview}
          upgradeOffer={upgradeOffer}
          provideContext={false}
          toggleDrawer={toggleDrawer}
        />
      ) : (
        <SpinnerContainer>
          <Spinner spinnerSize="32px" />
        </SpinnerContainer>
      )}
      <Modal
        isShowing={isModalShowing}
        toggleHide={handleCancelClose}
        showDefaultCloseBtn={false}
        canSkipModal={false}
        header="Cancel the change?"
        body="You’ll keep your current payment method."
        actions={modalActions}
        zIndex={20}
      />
    </React.Fragment>
  );
};
export default ChangePaymentMethodDrawer;
